import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { PageHeader, Pill } from "../components/Table";
import { Broadcast, PlugsConnected, WifiHigh, WifiSlash, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

const fmtAgo = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function Stat({ label, value, color, testid }) {
  return (
    <div className="rounded-md p-4 text-white shadow-sm" style={{ background: color }} data-testid={testid}>
      <div className="text-3xl font-black leading-none">{value}</div>
      <div className="text-xs font-semibold mt-1.5 opacity-90 uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default function LiveMonitor() {
  const [data, setData] = useState(null);
  const [auto, setAuto] = useState(true);
  const [pulse, setPulse] = useState(false);
  const timer = useRef();

  const load = async () => {
    try {
      const r = await api.get("/gateway/live-monitor");
      setData(r.data);
      setPulse(true); setTimeout(() => setPulse(false), 600);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    clearInterval(timer.current);
    if (auto) timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [auto]);

  const brands = data?.brands || [];
  const liveBrands = brands.filter((b) => b.last5min > 0).length;

  return (
    <div className="p-5 space-y-4" data-testid="live-monitor-page">
      <PageHeader title="Live Device Monitor" subtitle="Real-time packet feed per brand port — spot trackers that go silent"
        actions={
          <button data-testid="monitor-auto" onClick={() => setAuto((a) => !a)}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-sm text-white ${auto ? "bg-[#34C759]" : "bg-slate-500"}`}>
            <span className={`w-2 h-2 rounded-full bg-white ${auto ? "animate-pulse" : ""}`} />
            {auto ? "Live · auto-refresh 5s" : "Paused"}
          </button>
        } />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="stat-packets" label="Total packets" value={data?.total_packets ?? "—"} color="#0091e6" />
        <Stat testid="stat-listening" label="Ports listening" value={data?.listening_count ?? "—"} color="#34C759" />
        <Stat testid="stat-live" label="Brands receiving" value={liveBrands} color="#7C3AED" />
        <Stat testid="stat-silent" label="Silent trackers" value={data?.silent_devices?.length ?? "—"} color="#FF9500" />
      </div>

      {/* Brand ports */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2"><PlugsConnected size={16} className="text-primary" /> <span className="font-bold text-sm">Brand ports</span>
          <span className="text-xs text-muted-foreground ml-auto">Default TCP gateway: <span className="font-mono">:{data?.gateway_port}</span></span></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-secondary">
              {["Brand", "Protocol", "Port", "Status", "Last 5 min", "Total", "Last packet"].map((h) => <th key={h} className="text-left label-caps px-3 py-2.5 whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {brands.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No GPS brands configured yet — add brands in Inventory to open per-brand ports.</td></tr>}
              {brands.map((b) => {
                const receiving = b.last5min > 0;
                return (
                  <tr key={b.port} className="border-b border-border/60" data-testid={`brand-row-${b.port}`}>
                    <td className="px-3 py-2.5 font-semibold">{b.name}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{b.protocol}</td>
                    <td className="px-3 py-2.5 font-mono">{b.port}</td>
                    <td className="px-3 py-2.5">
                      {b.listening
                        ? <span className="inline-flex items-center gap-1 text-[#34C759] text-xs font-semibold"><WifiHigh size={14} weight="bold" /> Listening</span>
                        : <span className="inline-flex items-center gap-1 text-destructive text-xs font-semibold"><WifiSlash size={14} weight="bold" /> Down</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1.5 ${receiving ? "text-[#34C759] font-bold" : "text-muted-foreground"}`}>
                        {receiving && <span className="w-2 h-2 rounded-full bg-[#34C759] animate-pulse" />}{b.last5min}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{b.total}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtAgo(b.last_packet_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Live feed */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2"><Broadcast size={16} className={`text-primary ${pulse ? "animate-ping-once" : ""}`} /> <span className="font-bold text-sm">Live packet feed</span>
            <span className="text-xs text-muted-foreground ml-auto">latest 30</span></div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-border/60" data-testid="live-feed">
            {(data?.recent || []).length === 0 && <div className="px-4 py-8 text-center text-muted-foreground text-sm">Waiting for packets…</div>}
            {(data?.recent || []).map((p, i) => (
              <div key={p.id || i} className="px-4 py-2 flex items-center gap-3 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${p.recognized ? "bg-[#34C759]" : "bg-[#FF9500]"}`} />
                <span className="font-mono font-semibold">{p.imei || "?"}</span>
                <Pill color="#0091e6">{p.protocol}</Pill>
                {!p.recognized && <span className="text-[#FF9500] font-semibold">unrecognized</span>}
                <span className="text-muted-foreground ml-auto">{fmtAgo(p.received_at)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Silent trackers */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2"><WarningCircle size={16} className="text-[#FF9500]" /> <span className="font-bold text-sm">Silent trackers</span>
            <span className="text-xs text-muted-foreground ml-auto">no packet &gt; 10 min</span></div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-border/60" data-testid="silent-list">
            {(data?.silent_devices || []).length === 0 && <div className="px-4 py-8 text-center text-muted-foreground text-sm">All active trackers are reporting normally.</div>}
            {(data?.silent_devices || []).map((d) => (
              <div key={d.imei} className="px-4 py-2.5 flex items-center gap-3 text-xs" data-testid={`silent-${d.imei}`}>
                <WifiSlash size={14} className="text-[#FF9500]" />
                <span className="font-semibold">{d.vehicle_number || "Unassigned"}</span>
                <span className="font-mono text-muted-foreground">{d.imei}</span>
                <span className="ml-auto text-[#FF9500] font-semibold">silent {d.minutes_silent}m</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
